import React, { FC } from 'react';
import styled , { css } from 'styled-components';

import App from 'models/app';
import { Screenshot } from 'types/app';
import { DEVICE_PLACEHOLDERS } from 'config';

import ImageContainer from 'components/v2/ImageContainer/index';
import { Link } from 'components/Common';

const DEFAULT_CLOCK_WIDTH = 56;
const DEFAULT_ACTIVE_BEZEL_WIDTH = 68;
const DEFAULT_PREMIUM_BEZEL_WIDTH = 62;

const DEVICES: any = DEVICE_PLACEHOLDERS;

interface ClockProps {
  clock: App,
  active?: boolean;
  deviceName: string;
  setFocusedClock: (id: string) => any;
  index: number;
  [key: string]: any;
}

interface PlaceholderProps {
  size: Screenshot;
  deviceName: string;
  index: number;
  link?: string;
}

export function sizeMixin (size: Screenshot, width: number) {
  let height;

  if(size) {
    height = size.height / (size.width / width);
  }

  if (height) {
    return css`
    width: ${width}px;
    height: ${height}px;

    @media (max-width: 359px) {
      width: ${width - width * 0.1}px;
      height: ${height - height * 0.1}px;
    }
  `;
  } else {
    return null;
  }
};

const Container = styled.div<any>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ size }) => sizeMixin(size, DEFAULT_ACTIVE_BEZEL_WIDTH)}
`;

const CenteredIcon = styled.img<any>`
  position: absolute;
  top: 50%;
  left: 50%;
  overflow: hidden;
  transform: translate(-50%, -50%);

  ${({ size, defaultWidth }) => sizeMixin(size, defaultWidth)}
`;

const ActiveIcon = ({ size, src }: any) =>
  <CenteredIcon src={src} size={size} defaultWidth={DEFAULT_ACTIVE_BEZEL_WIDTH} />;

const PremiumIcon = ({ size, src }: any) =>
  <CenteredIcon src={src} size={size} defaultWidth={DEFAULT_PREMIUM_BEZEL_WIDTH} />;

const ClockImage = styled(({ image, name, ...props }) => (
  <ImageContainer
    {...props}
    containerAttr={{
      width: image.width,
      height: image.height
    }}
    imageAttr={{
      alt: name,
      src: image.uri
    }}
  />
))`
  ${({ image }) => sizeMixin(image, DEFAULT_CLOCK_WIDTH)}

  position: absolute;
  top: 50%;
  left: 50%;
  overflow: hidden;
  transform: translate(-50%, -50%);
`;

const Clock: FC<ClockProps> = ({
  clock: {
    id,
    banners: [image] = [{}],
    name,
    isPremium
  },
  active,
  deviceName,
  setFocusedClock,
  index,
  ...props
}) => {
  const activeClockIcon = DEVICES[deviceName]?.activeClockIcon;
  const premiumClockIcon = DEVICES[deviceName]?.premiumClockIcon;

  return (
    <Container size={image}>
      {active && activeClockIcon && <ActiveIcon src={activeClockIcon} size={image} />}
      {isPremium && <PremiumIcon src={premiumClockIcon} size={image} />}
      <ClockImage
        id={active ? 'clock-active' : `clock-${index}`}
        aria-label={active ? 'clock-active' : `clock-${index}`}
        image={image}
        name={name}
        onClick={() => setFocusedClock(id)}
        {...props}
      />
    </Container>
  )
};

const PlaceholderIcon = styled.div.attrs<any>(({ deviceIcon }) => ({
  dangerouslySetInnerHTML: { __html: deviceIcon }
}))<any>`
  position: relative;
  box-sizing: border-box;

  ${({ size }) => sizeMixin(size, DEFAULT_CLOCK_WIDTH)}
`;

export const Placeholder: FC<PlaceholderProps> = ({
  size,
  deviceName,
  index,
  link = '/v2/clocks/gallery'
}) => {
  const deviceIcon = DEVICES[deviceName]?.emptySlot;
  if (!deviceIcon) {
    return null;
  }

  return (
    <Container size={size}>
      <Link
        id={`placeholder-${index}`}
        aria-label={`placeholder-${index}`}
        to={link}
        pageDirection='FORWARD'
      >
        <PlaceholderIcon size={size} deviceIcon={deviceIcon} />
      </Link>
    </Container>
  );
};

export default Clock;
